import styled from 'styled-components';
import { Wrapper as SectionWrapper, themeOptions } from '../Level3Section';
import { useEffect, useMemo, useState } from 'react';
import { SendIcon } from '../icons/send';
import { SyncManager } from 'db';
import { useField } from 'react-final-form';
import { useFormVehicleId } from '../hooks/useFormVehicleId';
import { NotesSavedCheckmark } from 'components/VehicleCommon/icons/NotesSavedChekmark';
import { useTextareaEffects } from 'hooks/useTextareaEffects';

type Props = {
  theme: keyof typeof themeOptions;
};

const Header = styled.div`
  font-family: Roboto;
  font-weight: 600;
  font-size: 14px;
  line-height: 100%;
  letter-spacing: 0%;
  padding: 6px 8px 8px;
`;

const InputWrapper = styled.div<{ hasError: boolean }>`
  outline: ${props => (props.hasError ? '1px solid #AF2930' : '1px solid #999999')};
  border-radius: 8px;
  background: #fff;
  padding: 12px 8px;
  display: flex;
  align-items: center;
  margin: 8px;
`;

const Input = styled.textarea`
  border: none;
  font-size: 12px;
  line-height: 16px;
  width: 80%;
  font-weight: 500;
  color: #000;
  padding: 0;
  font-family: Roboto;
`;

const IconWrapper = styled.div<{ withAutoMargin?: boolean }>`
  display: flex;
  margin-left: ${props => (props.withAutoMargin ? 'auto' : 0)};
`;

const StyledError = styled.span`
  font-size: 10px;
  color: #e93323;
  padding: 0 8px;
`;

export const InspectorComments = ({ theme }: Props) => {
  const { background } = themeOptions[theme];
  const id = useFormVehicleId();

  const { input } = useField(`inspector_notes.message`);

  const [comment, setComment] = useState(input.value || '');
  const [error, setError] = useState<string | null>(null);

  const saveComment = async () => {
    if (!!error) return;
    input.onChange(comment);

    const syncManager = SyncManager.getInstance(id);
    await syncManager.queueVehicleUpdate(['inspector_notes.notes', comment]);
  };

  const textAreaRef = useTextareaEffects(comment);

  useEffect(() => {
    if (comment.length > 500) {
      setError('Max length is 500 characters');
    } else {
      setError(null);
    }
  }, [comment]);

  const statusIcon = useMemo(() => {
    if (!comment && !input.value) return null;
    return comment === input.value ? <NotesSavedCheckmark /> : <SendIcon />;
  }, [comment, input.value]);

  return (
    <SectionWrapper background={background} withBorder={theme === 'white'}>
      <Header>Inspector Comments</Header>
      <InputWrapper hasError={!!error}>
        <Input
          ref={textAreaRef}
          placeholder="Tap here to edit"
          value={comment}
          rows={1}
          onChange={e => setComment(e.target.value)}
        />
        <IconWrapper onClick={saveComment} withAutoMargin>
          {statusIcon}
        </IconWrapper>
      </InputWrapper>
      {error && <StyledError>{error}</StyledError>}
    </SectionWrapper>
  );
};
